// مشترک صفحات MED (AP-MED-01): شناسه صفحات و اجزا، مسیرها، کلیدهای Query، نمایش هشدار ایمنی با نشان «منبع نمونه» (IsSample — CLAUDE.md §10)،
// چاپ PDF و برچسب‌های وضعیت. متن‌ها فقط از فضای نام med.
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  type MedOrderStatus,
  type MedPrescriptionStatus,
  type MedReferralStatus,
  type MedResultFlag,
  type MedSafetyAlert,
  type MedSafetyCheckResult,
} from '@phoenix/api-client';
import { useTranslation } from '@phoenix/i18n';
import { SampleBadge, StatusChip, type StatusTone } from '@phoenix/ui';

/** نسخه‌های مراجعه‌کننده (US-MED-013). */
export const SCR_STAFF_700 = 'SCR-STAFF-700';
/** درخواست‌ها و نتایج (US-MED-044، 050، 053). */
export const SCR_STAFF_702 = 'SCR-STAFF-702';
/** ارجاع‌ها: ورودی، خروجی، پیگیری (US-MED-061، 062، 066). */
export const SCR_STAFF_705 = 'SCR-STAFF-705';
/** تنظیمات: فهرست دارو، نسخه‌های آماده، پنل‌ها و مراکز (US-MED-002، 003، 041، 042). */
export const SCR_STAFF_706 = 'SCR-STAFF-706';
/** تأیید اصالت عمومی نسخه و برگه درخواست (QR). */
export const SCR_PUBLIC_014 = 'SCR-PUBLIC-014';
export const COMP_RX_EDITOR = 'COMP-RX-EDITOR';
export const COMP_ORDER_EDITOR = 'COMP-ORDER-EDITOR';
export const COMP_REFERRAL_EDITOR = 'COMP-REFERRAL-EDITOR';

export const ORDERS_PATH = '/desk/orders';
export const REFERRALS_PATH = '/desk/referrals';
export const MED_SETTINGS_PATH = '/settings/medical-orders';
export const API = '/api/v1/medical-orders';

export const medKeys = {
  all: (tenantId?: string) => ['medorders', tenantId] as const,
  patient: (tenantId: string | undefined, patientId: string, section: string) => ['medorders', tenantId, 'patient', patientId, section] as const,
  prescription: (tenantId: string | undefined, id: string) => ['medorders', tenantId, 'prescription', id] as const,
  drugs: (tenantId: string | undefined, query: string) => ['medorders', tenantId, 'drugs', query] as const,
  templates: (tenantId?: string) => ['medorders', tenantId, 'templates'] as const,
  labTests: (tenantId: string | undefined, query: string) => ['medorders', tenantId, 'lab-tests', query] as const,
  panels: (tenantId?: string) => ['medorders', tenantId, 'panels'] as const,
  partners: (tenantId?: string) => ['medorders', tenantId, 'partners'] as const,
  drugList: (tenantId?: string) => ['medorders', tenantId, 'drug-list'] as const,
  result: (tenantId: string | undefined, id: string) => ['medorders', tenantId, 'result', id] as const,
  referrals: (tenantId: string | undefined, box: string) => ['medorders', tenantId, 'referrals', box] as const,
  referral: (tenantId: string | undefined, id: string) => ['medorders', tenantId, 'referral', id] as const,
  targets: (tenantId?: string) => ['medorders', tenantId, 'targets'] as const,
  queue: (tenantId?: string) => ['medorders', tenantId, 'queue'] as const,
  myPending: (tenantId?: string) => ['medorders', tenantId, 'my-pending'] as const,
  myOverdue: (tenantId?: string) => ['medorders', tenantId, 'my-overdue'] as const,
  referrers: (tenantId?: string) => ['medorders', tenantId, 'referrers'] as const,
  inbound: (tenantId: string | undefined, patientId: string | null) => ['medorders', tenantId, 'inbound', patientId] as const,
  analytes: (tenantId: string | undefined, patientId: string) => ['medorders', tenantId, 'patient', patientId, 'analytes'] as const,
  series: (tenantId: string | undefined, patientId: string, code: string) => ['medorders', tenantId, 'patient', patientId, 'series', code] as const,
};

/** عدد از قرارداد (int/decimal ممکن است رشته باشد). */
export function num(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** ذخیره یا باز کردن Blob چاپ. */
export function saveBlob(blob: Blob, fileName: string) {
  if (typeof URL.createObjectURL !== 'function') {
    return;
  }
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = fileName;
  anchor.rel = 'noopener';
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

const RX_TONES: Record<MedPrescriptionStatus, StatusTone> = { Draft: 'pending', PendingApproval: 'booked', Issued: 'done', Voided: 'cancelled', Discarded: 'inactive' };
const ORDER_TONES: Record<MedOrderStatus, StatusTone> = {
  Ordered: 'booked',
  Sent: 'confirmed',
  SampleCollected: 'arrived',
  InProgress: 'in_service',
  PartiallyResulted: 'in_service',
  Resulted: 'pending',
  Reviewed: 'done',
  Cancelled: 'cancelled',
};
const REFERRAL_TONES: Record<MedReferralStatus, StatusTone> = {
  Created: 'pending',
  Sent: 'booked',
  Accepted: 'confirmed',
  Declined: 'error',
  Scheduled: 'arrived',
  Responded: 'done',
  Closed: 'inactive',
  Cancelled: 'cancelled',
};

export function RxStatusChip({ status }: { status: MedPrescriptionStatus }) {
  return <StatusChip tone={RX_TONES[status]} labelKey={`med:rx_status.${status}`} />;
}

export function OrderStatusChip({ status }: { status: MedOrderStatus }) {
  return <StatusChip tone={ORDER_TONES[status]} labelKey={`med:order_status.${status}`} />;
}

export function ReferralStatusChip({ status }: { status: MedReferralStatus }) {
  return <StatusChip tone={REFERRAL_TONES[status]} labelKey={`med:referral_status.${status}`} />;
}

/** پرچم مقدار نتیجه (بحرانی قرمز، غیرطبیعی زرد). */
export function FlagChip({ flag }: { flag: MedResultFlag | null | undefined }) {
  const { t } = useTranslation('med');
  if (!flag || flag === 'Normal') {
    return flag ? <Chip size="small" variant="outlined" label={t('med:flag.Normal')} /> : null;
  }
  const critical = flag === 'CriticalLow' || flag === 'CriticalHigh';
  return <Chip size="small" color={critical ? 'error' : 'warning'} label={t(`med:flag.${flag}`)} data-testid="med-flag" data-flag={flag} />;
}

/** متن هشدار ایمنی از کلید پیام سرور (med:safety.message.*). */
export function alertText(t: (key: string, values?: Record<string, unknown>) => string, alert: MedSafetyAlert): string {
  return t(alert.messageKey, { subject: alert.subject ?? alert.drugCodes.join(t('med:common.list_separator')), defaultValue: alert.messageKey });
}

/**
 * COMP-SAFETY-ALERTS: هشدارهای ایمنی نسخه با شدت، منبع نمونه (IsSample) و اقدام Override برای Critical/Warning تعیین‌تکلیف‌نشده
 * (BR-MED-020، 021). آلرژی دارویی منبع نمونه نیست (C-AGENT-866).
 */
export function SafetyAlerts({ safety, onOverride, canOverride }: { safety: MedSafetyCheckResult | null | undefined; onOverride?: (alert: MedSafetyAlert) => void; canOverride: boolean }) {
  const { t } = useTranslation('med');
  if (!safety) {
    return null;
  }
  if (safety.alerts.length === 0) {
    return (
      <Alert severity="success" data-testid="med-safety-none">
        {t('med:safety.none')} {safety.knowledgeIsSample ? <SampleBadge /> : null}
      </Alert>
    );
  }
  return (
    <Box role="region" aria-label={t('med:safety.title')} data-testid="med-safety">
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', mb: 0.5 }}>
        <Typography variant="subtitle2" component="h4">
          {t('med:safety.title')}
        </Typography>
        {safety.knowledgeIsSample ? (
          <Chip size="small" variant="outlined" color="warning" label={t('med:safety.sample_source')} data-testid="med-safety-sample-source" />
        ) : null}
      </Stack>
      <Stack spacing={0.5}>
        {safety.alerts.map((alert) => (
          <Alert
            key={alert.key}
            severity={alert.severity === 'Critical' ? 'error' : alert.severity === 'Warning' ? 'warning' : 'info'}
            data-testid="med-safety-alert"
            data-severity={alert.severity}
            data-type={alert.type}
            action={
              alert.overridden ? (
                <Chip size="small" label={t('med:safety.overridden')} data-testid="med-safety-overridden" />
              ) : alert.severity !== 'Info' && onOverride && canOverride ? (
                <Button color="inherit" size="small" onClick={() => onOverride(alert)} data-testid="med-safety-override">
                  {t('med:safety.override')}
                </Button>
              ) : null
            }
          >
            <strong>{t(`med:severity.${alert.severity}`)}</strong> · {t(`med:alert_type.${alert.type}`)} · {alertText(t, alert)} {alert.isSample ? <SampleBadge /> : null}
          </Alert>
        ))}
      </Stack>
      {safety.alerts.some((alert) => alert.severity === 'Critical' && !alert.overridden) && (
        <Typography variant="body2" color="error" sx={{ mt: 0.5 }} data-testid="med-safety-blocked">
          {t('med:safety.blocked')}
        </Typography>
      )}
    </Box>
  );
}
